import { translate, useLanguage } from "@cqt/platform";
/**
 * The account home: which companies you can reach.
 *
 * One login, many companies — the model people already know from Airtable, and
 * the one the platform being replaced had. This is the only screen that is
 * about a PERSON rather than a company.
 *
 * Each card is a LINK to that company's own address, not a button that asks the
 * server to switch. That is the whole tenancy design in one interaction: the
 * address is the boundary, so entering a company means going there. The cost is
 * that the URL changes; the benefit is that no bug in this code, and no crafted
 * request, can put you in a company you are not in — there is nothing to craft.
 */
import type React from "react";

import { accentInk, useAuth, useMe } from "@cqt/platform";
import type { CompanyAccess } from "@cqt/platform";

function CompanyCard({ company }: { company: CompanyAccess }) {
  useLanguage();
  const initials = company.name
    .split(/\s+/).filter(Boolean).slice(0, 2)
    .map((w) => w[0]).join("").toUpperCase();

  // Each card wears its OWN company's colours, which is how you find a company
  // here before you have read its name. The accent arrives already filtered by
  // the server's allowlist, so it is a plain colour or it is absent — nothing
  // here has to defend against what a company typed into a branding field.
  //
  // The ink on the mark is COMPUTED from the accent, never chosen: the same
  // function the shell uses, for the same reason. A fixed dark ink put Ingress
  // at 1.63:1 on its own colour, and no branding screen could have fixed it.
  const accent = company.brand.accent;
  const style = accent
    ? ({
        "--accent": accent,
        "--accent-ink": accentInk(accent) ?? undefined,
      } as React.CSSProperties)
    : undefined;

  const mark = company.brand.logoUrl ? (
    <img className="company-card__mark company-card__mark--logo"
         src={company.brand.logoUrl} alt="" aria-hidden="true" />
  ) : (
    <span className="company-card__mark" aria-hidden="true">{initials}</span>
  );

  const inner = (
    <>
      {mark}
      <span className="company-card__body">
        <span className="company-card__name">{company.name}</span>
        {/* Two elements, not one string. As one string the whole line ellipsised
            together, so a company on a long temporary Railway hostname lost its
            tool count off the right-hand edge — the half of the line that is
            actually worth reading. The address truncates; the count never does. */}
        <span className="meta company-card__meta">
          <span className="company-card__addr">
            {company.address ?? translate("No web address yet")}
          </span>
          {company.tool_count ? (
            <span className="company-card__count">
              {translate(company.tool_count === 1 ? "{count} tool" : "{count} tools", {count:company.tool_count})}
            </span>
          ) : null}
        </span>
      </span>
    </>
  );

  // Without an address there is nowhere to send them, and a link that goes
  // nowhere is worse than a card that says why.
  if (!company.address) {
    return (
      <div className="company-card company-card--unreachable" style={style}
           aria-label={translate("{name} — not reachable yet", {name:company.name})}>
        {inner}
        <span className="chip">{translate("Not set up")}</span>
      </div>
    );
  }
  return (
    <a className="company-card" style={style} href={`https://${company.address}/`}>
      {inner}
      <span className="company-card__go" aria-hidden="true">→</span>
    </a>
  );
}

export function CompanyPicker() {
  useLanguage();
  const me = useMe();
  const { config } = useAuth();

  if (!me.companies.length) {
    return (
      <div className="card">
        <div className="empty">
          <div className="empty__title">{translate("No companies yet")}</div>
          <p style={{ maxWidth: 460, margin: 0 }}>
             {translate("Your account is set up, but nobody has added you to a company yet. An administrator does that, and then they appear here.")} </p>
          <p className="meta" style={{ marginTop: 4 }}>{translate("Signed in as")} {me.user.email}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <p className="muted" style={{ marginTop: -12, marginBottom: 20 }}>
        {me.companies.length === 1
          ? translate("You have access to one company.")
          : translate("You have access to {count} companies.", {count:me.companies.length})}{" "}
         {translate("Open one to use its tools.")} </p>
      <div className="company-cards">
        {me.companies.map((company) => (
          <CompanyCard key={company.id} company={company} />
        ))}
      </div>
      {me.is_superadmin ? (
        <p className="meta" style={{ marginTop: 18 }}>
           {translate("You are a platform administrator, so every active company is listed whether or not you are a member of it.")} </p>
      ) : null}
      <span hidden>{config.companyName}</span>
    </>
  );
}
